<template>
  <section class="commuter-access-oddity">
    <div class="w-full main-card px-3 md:px-4 lg:px-5 py-4 lg:py-5 xl:py-7">
      <div>
        <h2 class="!mb-1">Commuter Access Oddity</h2>
        <p class="text-text-light !mb-0">
          Members log at most 2 work trips a day, so an odd number of work trips usually means a
          trip is missing and more than 2 usually means duplicates. Pick an employer and a week to
          see which days have flagged members, in which Commuter Access category, and who they are.
        </p>
      </div>

      <div class="mt-5 flex flex-wrap items-end gap-4">
        <div class="flex-1 min-w-[280px]">
          <div class="block text-sm font-medium mb-1">Employer</div>
          <EmployerSelect v-model:value="employerId" />
        </div>
        <div>
          <div class="block text-sm font-medium mb-1">Week</div>
          <div class="flex items-center gap-2">
            <Button size="large" aria-label="Previous week" @click="shiftWeek(-1)">
              <template #icon>
                <PhArrowFatLeft :size="18" />
              </template>
            </Button>
            <DatePicker
              picker="week"
              size="large"
              :value="weekStart"
              :locale="weekPickerLocale"
              :format="formatWeek"
              :allow-clear="false"
              :disabled-date="disableFutureDate"
              @change="handleWeekChange"
            />
            <Button
              size="large"
              aria-label="Next week"
              :disabled="isCurrentWeek"
              @click="shiftWeek(1)"
            >
              <template #icon>
                <PhArrowFatRight :size="18" />
              </template>
            </Button>
          </div>
        </div>
      </div>

      <p v-if="!employerId" class="mt-6 text-text-light">
        Select an employer to see their week.
      </p>
      <div v-else-if="loading" class="flex items-center justify-center py-16">
        <Spin size="large" />
      </div>
      <template v-else-if="report">
        <div
          class="mt-6 oddity-kpi"
          :class="report.summary.flagged_user_days ? 'oddity-kpi--odd' : 'oddity-kpi--even'"
        >
          <template v-if="report.summary.flagged_user_days">
            {{ report.summary.flagged_user_days }} users flagged
            ({{ report.summary.odd_user_days }} odd ·
            {{ report.summary.over_limit_user_days }} more than 2) ·
            {{ report.summary.days_affected }} of 7 days affected
          </template>
          <template v-else>All days look right</template>
        </div>

        <div class="mt-4 overflow-x-auto">
          <table class="oddity-matrix">
            <thead>
              <tr>
                <th />
                <th v-for="day in report.days" :key="day.date">
                  <button
                    type="button"
                    class="oddity-matrix__day"
                    :class="{ 'is-selected': selectedDay === day.date }"
                    @click="toggleDay(day.date)"
                  >
                    <span class="block">{{ formatDay(day.date, 'ddd') }}</span>
                    <span class="block text-xs text-text-light">
                      {{ formatDay(day.date, 'MMM D') }}
                    </span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Total work trips</th>
                <td v-for="day in report.days" :key="day.date">{{ day.total_trips }}</td>
              </tr>
              <tr>
                <th>Users with 2 work trips</th>
                <td v-for="day in report.days" :key="day.date">{{ day.users_with_two_trips }}</td>
              </tr>
              <tr>
                <th>Users with 1 work trip</th>
                <td v-for="day in report.days" :key="day.date">{{ day.users_with_one_trip }}</td>
              </tr>
              <tr>
                <th>
                  Users with 0 work trips
                  <span class="font-normal text-xs text-text-light">
                    of {{ report.members_count }} members
                  </span>
                </th>
                <td v-for="day in report.days" :key="day.date">{{ day.users_without_trips }}</td>
              </tr>
              <tr>
                <th>Users with odd trips</th>
                <td
                  v-for="day in report.days"
                  :key="day.date"
                  :class="{ 'is-odd': day.odd_users > 0 }"
                >
                  {{ day.odd_users }}
                </td>
              </tr>
              <tr>
                <th>Users with more than 2 trips</th>
                <td
                  v-for="day in report.days"
                  :key="day.date"
                  :class="{ 'is-odd': day.over_limit_users > 0 }"
                >
                  {{ day.over_limit_users }}
                </td>
              </tr>
              <tr v-for="category in categories" :key="category.key">
                <th class="oddity-matrix__category">{{ category.label }}</th>
                <td
                  v-for="day in report.days"
                  :key="day.date"
                  :class="{ 'is-odd': day.flagged_users_by_category[category.key] > 0 }"
                >
                  {{ day.flagged_users_by_category[category.key] }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-6">
          <div class="flex items-center gap-2 mb-2">
            <h3 class="!mb-0">Flagged users</h3>
            <Tag v-if="selectedDay" closable @close="selectedDay = null">
              {{ formatDay(selectedDay, 'ddd, MMM D') }}
            </Tag>
          </div>
          <Table
            :data-source="visibleFlaggedUsers"
            :columns="columns"
            :row-key="(record) => `${record.date}-${record.user_id}`"
            :pagination="{ pageSize: 20, hideOnSinglePage: true }"
            :locale="{ emptyText }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'date'">
                <span class="whitespace-nowrap">{{ formatDay(record.date, 'ddd, MMM D') }}</span>
              </template>
              <template v-else-if="column.key === 'user'">
                <div>{{ record.name || '—' }}</div>
                <div class="text-xs text-text-light">{{ record.email }}</div>
              </template>
              <template v-else-if="column.key === 'issue'">
                <Tag v-if="record.odd" color="red">Odd</Tag>
                <Tag v-if="record.over_limit" color="orange">More than 2</Tag>
              </template>
              <template v-else-if="column.key === 'trips'">
                <!-- duplicated trips share a tripid, so key by position -->
                <div
                  v-for="(trip, index) in record.trips"
                  :key="index"
                  class="flex items-center gap-2"
                >
                  <span class="whitespace-nowrap">{{ trip.time }} · {{ trip.mode }}</span>
                  <Tag v-for="category in trip.categories" :key="category">
                    {{ categoryLabel(category) }}
                  </Tag>
                </div>
              </template>
            </template>
          </Table>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { Button, DatePicker, Spin, Table, Tag, message } from 'ant-design-vue';
import enGB from 'ant-design-vue/es/date-picker/locale/en_GB';
import { PhArrowFatLeft, PhArrowFatRight } from 'phosphor-vue';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import statisticsApi from '@/services/apis/admin/statistics.api';
import handleResErrors from '@/utils/handleResErrors';
import EmployerSelect from '@/components/admin/PlatformStats/EmployerSelect.vue';

// Same categories, order and labels as the employer's Commuter Access chart.
const CATEGORIES = [
  { key: 'website', label: 'Website trip submission' },
  { key: 'automatically_recorded', label: 'Commutrics automatic trip submission' },
  { key: 'mobile_app', label: 'Mobile apps - automatic trip recording' },
  { key: 'mobile_app_single_trip', label: 'Mobile apps trip submission' },
];

// Weeks run Monday to Sunday, like the Commuter Access chart.
const mondayOf = (date) => {
  const day = dayjs(date).startOf('day');
  return day.subtract((day.day() + 6) % 7, 'day');
};

export default {
  name: 'CommuterAccessOddity',
  components: {
    Button,
    DatePicker,
    Spin,
    Table,
    Tag,
    PhArrowFatLeft,
    PhArrowFatRight,
    EmployerSelect,
  },
  data() {
    return {
      employerId: undefined,
      weekStart: mondayOf(dayjs()),
      // en_GB starts the picker's weeks on Monday.
      weekPickerLocale: enGB,
      categories: CATEGORIES,
      report: null,
      loading: false,
      selectedDay: null,
      requestId: 0,
      columns: [
        { title: 'Date', key: 'date', width: 140 },
        { title: 'User', key: 'user' },
        { title: 'Work trips', dataIndex: 'trip_count', key: 'trip_count', width: 110 },
        { title: 'Issue', key: 'issue', width: 170 },
        { title: 'Trips', key: 'trips' },
      ],
    };
  },
  computed: {
    isCurrentWeek() {
      return !this.weekStart.isBefore(mondayOf(dayjs()), 'day');
    },
    visibleFlaggedUsers() {
      const flaggedUsers = this.report?.flagged_users || [];
      if (!this.selectedDay) return flaggedUsers;
      return flaggedUsers.filter((flaggedUser) => flaggedUser.date === this.selectedDay);
    },
    emptyText() {
      return this.selectedDay ? 'No flagged users on this day.' : 'No flagged users this week.';
    },
  },
  watch: {
    employerId() {
      this.fetchReport();
    },
    weekStart() {
      this.fetchReport();
    },
  },
  methods: {
    formatWeek(value) {
      const start = mondayOf(value);
      return `${start.format('MMM D')} – ${start.add(6, 'day').format('MMM D, YYYY')}`;
    },
    formatDay(date, format) {
      return dayjs(date).format(format);
    },
    categoryLabel(key) {
      return CATEGORIES.find((category) => category.key === key)?.label || key;
    },
    disableFutureDate(current) {
      return current && current.isAfter(mondayOf(dayjs()).add(6, 'day').endOf('day'));
    },
    handleWeekChange(value) {
      if (value) this.weekStart = mondayOf(value);
    },
    shiftWeek(weeks) {
      this.weekStart = this.weekStart.add(weeks * 7, 'day');
    },
    toggleDay(date) {
      this.selectedDay = this.selectedDay === date ? null : date;
    },
    async fetchReport() {
      this.selectedDay = null;
      if (!this.employerId) {
        this.report = null;
        return;
      }
      // Ignore responses from earlier selections that resolve late.
      this.requestId += 1;
      const { requestId } = this;
      this.loading = true;
      try {
        const report = await statisticsApi.fetchCommuterAccessOddity({
          employerId: this.employerId,
          weekStart: this.weekStart.format('YYYY-MM-DD'),
        });
        if (requestId === this.requestId) this.report = report;
      } catch (error) {
        if (requestId === this.requestId) {
          this.report = null;
          message.error(handleResErrors(error));
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.oddity-kpi {
  display: inline-block;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  font-weight: 600;
}

.oddity-kpi--odd {
  background: #fee2e2;
  color: #dc2626;
}

.oddity-kpi--even {
  background: #d1fae5;
  color: #059669;
}

.oddity-matrix {
  width: 100%;
  border-collapse: collapse;
}

.oddity-matrix th,
.oddity-matrix td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--color-bg-dark);
  text-align: center;
  white-space: nowrap;
}

.oddity-matrix tbody th {
  text-align: left;
  font-weight: 600;
}

.oddity-matrix tbody th.oddity-matrix__category {
  padding-left: 1.5rem;
  font-weight: 400;
}

.oddity-matrix td.is-odd {
  background: #fee2e2;
  color: #dc2626;
  font-weight: 600;
}

.oddity-matrix__day {
  width: 100%;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.oddity-matrix__day:hover,
.oddity-matrix__day.is-selected {
  background: var(--color-primary-outline);
  color: var(--color-primary);
}
</style>
